import React, { memo } from 'react'
import classNames from 'classnames'
import get from 'lodash/get'
import {
  createStyles,
  makeStyles,
  Typography,
  AutocompleteRenderGroupParams,
} from 'transfix-ui/core'
import { brandColors, calcRem } from '../../../../theme'
import { IAutocompleteBaseGroup } from '../../types'

interface IAutocompleteGroupProps {
  /**
   * Automatically injected params from `renderGroup` prop on <Autocomplete />
   */
  params: AutocompleteRenderGroupParams
  /**
   * @example { 1: { groupIndex: 1, groupName: 'Users', etc... }}
   */
  optionGroupsByGroupIndex?: Record<number, IAutocompleteBaseGroup>
}

/**
 * Gets group name based on keyed index from the group object
 */
function getGroup(
  optionGroupsByGroupIndex: Record<number, IAutocompleteBaseGroup> | undefined,
  groupIndex: string
): IAutocompleteBaseGroup | undefined {
  return get(optionGroupsByGroupIndex, groupIndex)
}

const useAutocompleteGroupStyles = makeStyles(() =>
  createStyles({
    title: {
      fontWeight: 600,
      fontSize: calcRem(12),
      lineHeight: 1.6,
      padding: '8px 10px',
      color: brandColors.coolGray8,
    },
    dividerBefore: {
      borderTop: `1px solid ${brandColors.coolGray4}`,
    },
    dividerAfter: {
      borderBottom: `1px solid ${brandColors.coolGray4}`,
    },
  })
)

/**
 * Used for <Autocomplete />'s `renderGroup` prop
 */
const AutocompleteGroup = memo<IAutocompleteGroupProps>(
  ({ optionGroupsByGroupIndex: groupsProp, params }) => {
    const { children, group: groupIndex, key } = params || {}
    const { dividerAfter, dividerBefore, groupName } = getGroup(groupsProp, groupIndex) || {}
    const classes = useAutocompleteGroupStyles()
    const className = classNames({
      [classes.dividerAfter]: dividerAfter,
      [classes.dividerBefore]: dividerBefore,
    })
    return (
      <div key={key} role='group' aria-label={groupIndex} className={className}>
        {groupName && (
          <Typography component='div' variant='caption' className={classes.title}>
            {groupName}
          </Typography>
        )}
        {children}
      </div>
    )
  }
)

export default AutocompleteGroup
